/**
 * useDemoStructuralKernel — shared TS-WASM kernel loader for demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useEffect, useMemo, useState } from "react";
import {
  createSynchronousStructuralKernel,
  loadDemoStructuralKernel,
  type DemoStructuralKernel,
} from "../domain/wasmSecurityGuarantees.js";

export interface DemoStructuralKernelState {
  readonly kernel: DemoStructuralKernel;
  readonly loading: boolean;
  readonly loadError: string | null;
}

export function useDemoStructuralKernel(): DemoStructuralKernelState {
  const fallbackKernel = useMemo(() => createSynchronousStructuralKernel(), []);
  const [loadedKernel, setLoadedKernel] = useState<DemoStructuralKernel | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadDemoStructuralKernel()
      .then((kernel) => {
        if (!cancelled) {
          setLoadedKernel(kernel);
          setLoadError(null);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : "Failed to load TS-WASM kernel");
        }
      });
    return (): void => {
      cancelled = true;
    };
  }, []);

  return {
    kernel: loadedKernel ?? fallbackKernel,
    loading: loadedKernel === null && loadError === null,
    loadError,
  };
}
